import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import {
  selectNewPostsAvailable,
  clearNewPostsAvailable,
  triggerFeedRefresh,
} from '../../slices/notificationsSlice';

/**
 * NewPostBanner
 * Clicking it navigates to the feed and triggers a refresh.
 */
const NewPostBanner = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const newPostsAvailable = useSelector(selectNewPostsAvailable);

  const handleClick = useCallback(() => {
    dispatch(clearNewPostsAvailable());

    if (location.pathname === '/') {
      // Already on home — scroll to top and trigger feed refetch
      window.scrollTo({ top: 0, behavior: 'smooth' });
      dispatch(triggerFeedRefresh());
    } else {
      // Navigate to home — fresh mount will fetch latest data
      navigate('/');
    }
  }, [dispatch, navigate, location.pathname]);

  return (
    <AnimatePresence>
      {newPostsAvailable > 0 && (
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          className="fixed top-20 sm:top-24 left-0 right-0 z-[100] flex justify-center pointer-events-none"
        >
          <button
            onClick={handleClick}
            className="
              pointer-events-auto
              flex items-center gap-2
              px-4 sm:px-5 py-2 sm:py-2.5
              bg-primary text-primary-foreground
              rounded-full
              shadow-lg shadow-primary/25
              text-xs sm:text-sm font-medium
              hover:bg-primary/90
              active:scale-95
              transition-all duration-200
              border border-primary/20
              backdrop-blur-sm
            "
            aria-label={t('new_posts_available', 'New posts available — tap to see')}
          >
            <ArrowUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>
              {newPostsAvailable === 1
                ? t('new_post_single', '1 new post')
                : t('new_posts_multiple', '{{count}} new posts', { count: newPostsAvailable })}
              {' — '}
              {t('tap_to_see', 'Tap to see')}
            </span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NewPostBanner;

